/**
 * Lightweight fetch-based API client.
 *
 * - Automatically attaches the JWT Bearer token from the auth store.
 * - Parses JSON responses.
 * - Maps 4xx / 5xx responses into thrown Error objects with the server message.
 * - On 401 attempts to refresh the access token once, then retries the request.
 * - If refresh fails, clears auth state (ADR-021 outside-React access pattern).
 */
import { useAuthStore } from '../state/useAuthStore';
import { logger } from './logger.client';

const log = logger.child({ module: 'apiClient' });

/** Try to get a new access token using the refresh token cookie. Returns null on failure. */
async function tryRefreshToken(): Promise<string | null> {
  try {
    const response = await fetch('/api/v1/auth/refresh', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) return null;

    const body = (await response.json()) as { success: boolean; data: { accessToken: string } };
    const newToken = body.data?.accessToken ?? null;

    if (newToken) {
      useAuthStore.setState((state) => {
        state.token = newToken;
      });
    }

    return newToken;
  } catch {
    return null;
  }
}

/**
 * Make an authenticated fetch call and return the parsed JSON body.
 *
 * @param url  - Relative URL (e.g. `/api/v1/resumes`).
 * @param init - Standard RequestInit overrides.
 * @param _retry - Internal flag to prevent infinite refresh loops.
 */
export async function apiClient<T>(
  url: string,
  init?: RequestInit,
  _retry = false,
): Promise<T> {
  const token = useAuthStore.getState().token;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init?.headers as Record<string, string> | undefined),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  log.debug('API request', { method: init?.method ?? 'GET', url });

  const response = await fetch(url, {
    ...init,
    credentials: 'include',
    headers,
  });

  /* On 401, attempt a one-time token refresh then retry */
  if (response.status === 401 && !_retry && !url.includes('/auth/refresh')) {
    log.warn('Received 401 -- attempting token refresh');
    const newToken = await tryRefreshToken();

    if (newToken) {
      log.info('Token refreshed -- retrying request');
      return apiClient<T>(url, init, true);
    }

    /* Refresh failed -- clear auth so the UI redirects to login */
    log.warn('Token refresh failed -- clearing auth state');
    useAuthStore.setState((state) => {
      state.token = null;
      state.user = null;
    });
  }

  if (!response.ok) {
    let errorMessage = `Request failed (${response.status})`;

    try {
      const body: unknown = await response.json();
      if (
        body &&
        typeof body === 'object' &&
        'message' in body &&
        typeof (body as { message: unknown }).message === 'string'
      ) {
        errorMessage = (body as { message: string }).message;
      }
    } catch {
      /* body was not JSON -- use the default message */
    }

    log.error('API error', {
      status: response.status,
      url,
      message: errorMessage,
    });
    throw new Error(errorMessage);
  }

  /* 204 No Content -- return undefined cast to T (for void returns) */
  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}
